import { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { Link } from 'react-router-dom'
import posthog from 'posthog-js'
import SupportIcon from 'assets/images/sidebar/cuntactSupport.svg'
import {
    getPlatformHint,
    getRememberedPlatform,
} from 'modules/miniapp/detect'

type TChannel = 'bale' | 'eitaa' | 'phone'

const CHANNEL_TITLE: Record<TChannel, string> = {
    bale: 'گفتگو در بله',
    eitaa: 'گفتگو در ایتا',
    phone: 'تماس تلفنی',
}

const CHANNEL_NAME: Record<TChannel, string> = {
    bale: 'بله',
    eitaa: 'ایتا',
    phone: 'تلفن',
}

/** The channels the manager set, the messenger we are running in first. */
export const setChannels = (support?: IFundSupport): TChannel[] => {
    if (!support) return []
    const here = getRememberedPlatform() ?? getPlatformHint()
    const order: TChannel[] =
        here === 'eitaa' ? ['eitaa', 'bale', 'phone'] : ['bale', 'eitaa', 'phone']
    return order.filter((channel) => !!support[channel])
}

/** «بله، ایتا یا تلفن» */
const joinNames = (channels: TChannel[]) => {
    const names = channels.map((channel) => CHANNEL_NAME[channel])
    if (names.length < 2) return names[0] ?? ''
    return `${names.slice(0, -1).join('، ')} یا ${names[names.length - 1]}`
}

const nudgeKey = (moneyPool: string) =>
    `hamyan.fundSupportNudgeDismissed.${moneyPool}`

const isNudgeDismissed = (moneyPool: string) => {
    try {
        return window.localStorage.getItem(nudgeKey(moneyPool)) === '1'
    } catch {
        return false
    }
}

/**
 * «پشتیبانی صندوق» on the fund page — the fund's own managers, not Hamyan's
 * support. The manager sets a Bale account, an Eitaa account and/or a phone
 * number (fund settings → «پشتیبانی صندوق»); every member gets a card with
 * «تماس با پشتیبانی صندوق», which lists the ones that are set.
 *
 * A manager of a fund that set none gets a one-time, closable nudge to the
 * settings screen instead; members of such a fund see nothing.
 */
export default function FundSupportCard({
    moneyPool,
    fundName,
    support,
    isManager,
    role,
}: {
    moneyPool: string | undefined
    fundName?: string
    support?: IFundSupport
    isManager: boolean
    role?: string
}) {
    const [open, setOpen] = useState(false)
    const [nudgeDismissed, setNudgeDismissed] = useState(
        () => !!moneyPool && isNudgeDismissed(moneyPool)
    )
    const channels = setChannels(support)

    // An older backend sends no block at all — nothing to offer, nothing to
    // nag about either.
    if (!moneyPool || !support) return null

    if (!channels.length) {
        if (!isManager || nudgeDismissed) return null
        const close = () => {
            setNudgeDismissed(true)
            try {
                window.localStorage.setItem(nudgeKey(moneyPool), '1')
            } catch {
                /* shown again next time */
            }
            posthog.capture('fund_support_nudge_dismissed', {
                fund_id: Number(moneyPool),
            })
        }
        return (
            <div
                className="relative mx-4 mb-[18px] rounded-xl border border-brand-soft bg-brand-soft p-3 pl-8 text-xs leading-6 text-text-body"
                data-testid="fund-support-nudge"
            >
                <button
                    type="button"
                    onClick={close}
                    aria-label="بستن"
                    className="absolute left-2 top-2 text-text-secondary"
                >
                    ✕
                </button>
                <p className="font-bold text-brand-deep">پشتیبانی صندوق</p>
                <p className="mt-1">
                    حساب بله، ایتا یا شماره تلفنی بگذارید تا اعضا سؤال‌هایشان
                    را مستقیم از خودتان بپرسند.
                </p>
                <Link
                    to={`/fund/setting/support/${moneyPool}`}
                    onClick={() =>
                        posthog.capture('fund_support_nudge_clicked', {
                            fund_id: Number(moneyPool),
                        })
                    }
                    className="mt-2 inline-block font-bold text-brand"
                >
                    تنظیم پشتیبانی صندوق ←
                </Link>
            </div>
        )
    }

    const openModal = () => {
        setOpen(true)
        posthog.capture('fund_support_opened', {
            fund_id: Number(moneyPool),
            role,
            has_bale: !!support.bale,
            has_eitaa: !!support.eitaa,
            has_phone: !!support.phone,
        })
    }

    const choose = (channel: TChannel) => {
        posthog.capture('fund_support_channel_clicked', {
            fund_id: Number(moneyPool),
            channel,
            role,
        })
        setOpen(false)
    }

    return (
        <>
            <div
                className="mx-4 mb-[18px] rounded-xl border border-[#E4E7EC] bg-white p-3"
                data-testid="fund-support-card"
            >
                <div className="flex items-start gap-3">
                    <img
                        src={SupportIcon}
                        alt=""
                        className="mt-0.5 h-6 w-6 shrink-0"
                    />
                    <div className="min-w-0 grow">
                        <p className="text-sm font-bold">پشتیبانی صندوق</p>
                        <p className="mt-1 text-xs leading-5 text-text-secondary">
                            سؤال یا مشکلی دربارهٔ صندوق دارید؟ از راه{' '}
                            {joinNames(channels)} با مدیر صندوق در ارتباط
                            باشید.
                        </p>
                    </div>
                    {isManager && (
                        <Link
                            to={`/fund/setting/support/${moneyPool}`}
                            className="shrink-0 text-xs font-bold text-brand no-underline"
                        >
                            ویرایش
                        </Link>
                    )}
                </div>
                <button
                    type="button"
                    className="mt-3 h-[31px] w-full rounded-[4px] bg-green-1 text-sm font-bold text-white active:scale-[0.98]"
                    onClick={openModal}
                >
                    تماس با پشتیبانی صندوق
                </button>
            </div>

            <Modal
                show={open}
                onHide={() => setOpen(false)}
                size="sm"
                aria-labelledby="fund-support-title"
                centered
            >
                <div className="w-full py-4 text-center text-xs">
                    <p id="fund-support-title" className="mb-1 px-3 text-sm font-bold">
                        پشتیبانی صندوق{fundName ? ` «${fundName}»` : ''}
                    </p>
                    <p className="mb-3 px-3 leading-5 text-text-secondary">
                        این راه‌ها را مدیر صندوق گذاشته است و به پشتیبانی همیان
                        وصل نیست.
                    </p>
                    {channels.map((channel) => {
                        const contact = support[channel] as IFundSupportChannel
                        const isPhone = channel === 'phone'
                        return (
                            <div key={channel}>
                                <hr />
                                {/* `inline-block` so the vertical margin
                                    applies — see SupportModal */}
                                <a
                                    href={contact.url}
                                    {...(isPhone
                                        ? {}
                                        : { target: '_blank', rel: 'noreferrer' })}
                                    onClick={() => choose(channel)}
                                    className="my-3 inline-block text-green-1 no-underline"
                                    data-testid={`fund-support-${channel}`}
                                >
                                    <span className="block">
                                        {CHANNEL_TITLE[channel]}
                                    </span>
                                    <span
                                        dir="ltr"
                                        className="mt-1 block text-[11px] text-text-secondary"
                                    >
                                        {contact.label}
                                    </span>
                                </a>
                            </div>
                        )
                    })}
                    <hr />
                    <p className="mt-4" onClick={() => setOpen(false)}>
                        انصراف
                    </p>
                </div>
            </Modal>
        </>
    )
}
